import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Github, Shield, Database, Image, Users, Mail, Newspaper } from 'lucide-react';
import './SoulProject.css';

function SpanProject() {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const features = [
        {
            icon: <Newspaper size={32} />,
            title: "Blog System",
            desc: "Full-featured blog with post creation, editing, comments, and newsletter subscriptions for mental health content"
        },
        {
            icon: <Image size={32} />,
            title: "Image Gallery",
            desc: "Cloudinary-powered gallery with automatic image optimization, CDN delivery, and on-the-fly transformations"
        },
        {
            icon: <Shield size={32} />,
            title: "Admin Panel",
            desc: "Secure JWT-authenticated admin dashboard for managing posts, gallery, testimonials, and site content"
        },
        {
            icon: <Database size={32} />,
            title: "SQLite Database",
            desc: "Zero-config SQLite via Turso/libSQL for cost-effective, ACID-compliant data storage with free cloud tier"
        },
        {
            icon: <Users size={32} />,
            title: "Testimonials",
            desc: "Community testimonials section to showcase impact and stories from people helped by SPAN programs"
        },
        {
            icon: <Mail size={32} />,
            title: "Newsletter",
            desc: "Email newsletter subscription system to keep supporters engaged with latest updates and awareness campaigns"
        }
    ];

    const techStack = [
        { category: "Frontend", items: ["HTML5", "CSS3", "Bootstrap 5", "jQuery", "Owl Carousel", "Font Awesome"] },
        { category: "Backend", items: ["Node.js", "Express.js", "JWT", "bcrypt", "Multer"] },
        { category: "Database", items: ["SQLite", "libSQL/Turso"] },
        { category: "Storage", items: ["Cloudinary", "CDN", "Image Optimization"] }
    ];

    return (
        <div className="soul-project">
            {/* Header */}
            <header className="project-header">
                <button onClick={() => navigate('/#projects')} className="back-btn">
                    <ArrowLeft size={20} /> Back to Portfolio
                </button>

                <div className="project-hero">
                    <span className="project-tag">Freelance Project</span>
                    <h1>SPAN Website</h1>
                    <h2>Suicide Prevention Awareness Network</h2>
                    <p className="tagline">
                        A full-stack web platform for a non-profit organization dedicated to mental health
                        awareness and suicide prevention programs, featuring a blog system, image gallery,
                        admin panel, and community engagement tools.
                    </p>
                </div>
            </header>

            {/* Overview Section */}
            <section className="section">
                <h3>Project Overview</h3>
                <div className="overview-content">
                    <p>
                        SPAN (Suicide Prevention Awareness Network - Research & Training) is a non-profit
                        organization focused on mental health awareness and suicide prevention. This project
                        involved building a complete web presence that balances cost-effectiveness with
                        functionality, ensuring the organization can manage content and engage with their
                        community without expensive hosting or maintenance overhead.
                    </p>
                    <p>
                        The platform combines a static frontend for fast, SEO-friendly page loads with a
                        Node.js/Express backend for dynamic content management. The admin panel allows
                        staff to manage blog posts, upload images, moderate comments, and view site
                        statistics—all secured with JWT authentication.
                    </p>
                </div>
            </section>

            {/* Features Section */}
            <section className="section">
                <h3>Key Features</h3>
                <div className="features-grid">
                    {features.map((feature, index) => (
                        <div key={index} className="feature-card">
                            <div className="feature-icon">{feature.icon}</div>
                            <h4>{feature.title}</h4>
                            <p>{feature.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Tech Stack Section */}
            <section className="section">
                <h3>Technology Stack</h3>
                <div className="tech-grid">
                    {techStack.map((tech, index) => (
                        <div key={index} className="tech-category">
                            <h4>{tech.category}</h4>
                            <div className="tech-items">
                                {tech.items.map((item, i) => (
                                    <span key={i} className="tech-badge">{item}</span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Why These Technologies */}
            <section className="section">
                <h3>Technology Decisions</h3>
                <div className="problem-solution">
                    <div className="ps-card problem">
                        <h4>Why SQLite (via Turso)?</h4>
                        <ul>
                            <li>Zero configuration, no external server dependencies</li>
                            <li>Single-file database for easy backup and replication</li>
                            <li>Generous free tier handles non-profit traffic</li>
                            <li>Full ACID compliance for data reliability</li>
                            <li>Same code works locally and in cloud production</li>
                        </ul>
                    </div>
                    <div className="ps-card solution">
                        <h4>Why Cloudinary?</h4>
                        <ul>
                            <li>Automatic image optimization for all devices</li>
                            <li>Global CDN ensures fast load times worldwide</li>
                            <li>On-the-fly transformations without multiple copies</li>
                            <li>Reduces bandwidth costs for charity website</li>
                            <li>Seamless Express/Multer integration</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Key Technical Features */}
            <section className="section">
                <h3>Technical Implementation</h3>
                <div className="problem-solution">
                    <div className="ps-card solution">
                        <h4>Security Features</h4>
                        <ul>
                            <li>JWT-based admin authentication</li>
                            <li>bcrypt password hashing</li>
                            <li>Multer for secure file uploads</li>
                            <li>Environment variable configuration</li>
                            <li>Role-based access control</li>
                        </ul>
                    </div>
                    <div className="ps-card solution">
                        <h4>Performance Optimizations</h4>
                        <ul>
                            <li>Static frontend for fast page loads</li>
                            <li>Cloudinary CDN for image delivery</li>
                            <li>Automatic image compression and resizing</li>
                            <li>Lazy loading for gallery images</li>
                            <li>Responsive design for all devices</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Deployment */}
            <section className="section">
                <h3>Deployment Strategy</h3>
                <div className="overview-content">
                    <p>
                        Both the backend API and frontend are hosted on Netlify as serverless functions,
                        providing a unified deployment pipeline with automatic deployments from Git.
                        The architecture ensures zero ongoing costs for the non-profit organization through free
                        tiers of Netlify, Turso, and Cloudinary.
                    </p>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '1rem',
                        marginTop: '1.5rem'
                    }}>
                        {[
                            { label: "Hosting", value: "Netlify Serverless" },
                            { label: "Frontend", value: "Netlify Functions" },
                            { label: "Database", value: "Turso (Free Tier)" },
                            { label: "Images", value: "Cloudinary (Free Tier)" }
                        ].map((item, index) => (
                            <div key={index} style={{
                                background: 'rgba(0, 245, 255, 0.1)',
                                padding: '1rem',
                                borderRadius: '12px',
                                border: '1px solid rgba(0, 245, 255, 0.2)',
                                textAlign: 'center'
                            }}>
                                <div style={{ color: 'var(--primary)', fontWeight: '600', fontSize: '0.85rem' }}>{item.label}</div>
                                <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '0.3rem' }}>{item.value}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Project Links */}
            <section className="section">
                <div className="project-links">
                    <a href="https://github.com/ANJOEJAMES" target="_blank" rel="noreferrer" className="project-btn">
                        <Github size={20} /> View on GitHub
                    </a>
                    <button onClick={() => navigate('/#projects')} className="project-btn secondary">
                        <ArrowLeft size={20} /> Back to Portfolio
                    </button>
                </div>
            </section>

            {/* Footer */}
            <footer className="project-footer">
                <p>© {new Date().getFullYear()} Anjoe James. SPAN Website Project.</p>
            </footer>
        </div>
    );
}

export default SpanProject;
